import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MachineListDto } from '../../models/machines/machine-list-dto';
import { MachineDetailsDto } from '../../models/machines/machine-details-dto';
import { SnackbarService } from '../error/snackbar.service';
import { SnackbarConfig, SnackbarPosition, SnackbarType } from '../../enums/snackbar-config';
import { LoggingApplicationInsightsService } from '../logging-application-insights/logging-application-insights.service';
import { Operation } from 'fast-json-patch';
import { UpdateMachineModel } from '../../models/machines/update-machine-model';
import { browserOnly } from './browser-only.util';

@Injectable({
  providedIn: 'root'
})
export class MachineApiService {
  private apiUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private snackBarService: SnackbarService,
    private loggingApplicationInsights: LoggingApplicationInsightsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  getMachineList(): Observable<MachineListDto[]> {
    return browserOnly<MachineListDto[]>(this.platformId, [], () => {
      const start = performance.now();

      return this.http.get<MachineListDto[]>(`${this.apiUrl}/machines`).pipe(
      tap(() => {
        const end = performance.now();
        this.loggingApplicationInsights.logMetric('getMachineList', (end - start), { origin: this.constructor.name });
      }),
      catchError(error => this.snackBarService.handleApiError(error))
      );
    });
  }

  getMachine(id: number): Observable<MachineDetailsDto> {
    return browserOnly<MachineDetailsDto>(this.platformId, {} as MachineDetailsDto, () => this.http.get<MachineDetailsDto>(`${this.apiUrl}/machine/${id}`).pipe(catchError(error => this.snackBarService.handleApiError(error))));
  }

  updateMachine(machine: UpdateMachineModel): Observable<number> {
    return browserOnly<number>(this.platformId, 0, () => this.http.put<number>(`${this.apiUrl}/machine/${machine.id}`, machine, {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    }).pipe(tap(() => {
      this.snackBarService.showResponse(`Maskin ${machine.machineId} sparad!`, SnackbarConfig.combine(SnackbarType.SUCCESS, SnackbarPosition.BOTTOM))
    }),
      catchError(error => this.snackBarService.handleApiError(error))
    ))
  }

  patchMachine(id: number, machineIdentifier: string, operations: Operation[]): Observable<number> {
    return browserOnly<number>(this.platformId, 0, () => this.http.patch<number>(`${this.apiUrl}/machine/${id}`, operations, {
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json"
      })
    }).pipe(tap(() => {
      this.snackBarService.showResponse(`Maskin ${machineIdentifier} sparad!`, SnackbarConfig.combine(SnackbarType.SUCCESS, SnackbarPosition.BOTTOM))
    }),
      catchError(error => this.snackBarService.handleApiError(error))
    ));
  }

  duplicateMachine(machineId: number): Observable<number> {
    return browserOnly<number>(this.platformId, 0, () => this.http.post<number>(`${this.apiUrl}/machine/duplicate/${machineId}`, null, {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    }).pipe(tap(() => {
      this.snackBarService.showResponse("Ny maskin skapad som kopia", SnackbarConfig.combine(SnackbarType.SUCCESS, SnackbarPosition.BOTTOM))
    }),
      catchError(error => this.snackBarService.handleApiError(error))
    ))
  }

  checkGpxIdExists(gpxId: string, excludeMachineId: number = 0): Observable<boolean> {
    return browserOnly<boolean>(this.platformId, false, () => this.http.get<boolean>(`${this.apiUrl}/machine/validatesdcgpxid/${gpxId}/excludeMachineId=${excludeMachineId}`).pipe(
      catchError(error => this.snackBarService.handleApiError(error))
    ));
  }
}